import { baseApi } from "./baseApi";
import type {
  Applicant,
  ApplicantDetail,
  ApplicantsResponse,
  ApplicantStats,
  AiSummary,
  MatchScoreDetail,
  AiSummaryInterview,
  SuggestedInterviewQuestions,
  HireDetails,
} from "../../../shared/api/applicants";

export type {
  Applicant,
  ApplicantDetail,
  ApplicantsResponse,
  ApplicantStats,
  AiSummary,
  MatchScoreDetail,
  AiSummaryInterview,
  SuggestedInterviewQuestion,
  SuggestedInterviewQuestions,
  HireDetails,
} from "../../../shared/api/applicants";
import type { ScopeFilterParams } from "../../../shared/types/accessScope";
import type { GreythrGender } from "../../../shared/types/greythr";

/** Location, group and department filters are the scope filter's ids (D16). */
interface ListApplicantsParams extends ScopeFilterParams {
  instanceId: string;
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  stageCategory?: string;
  jobId?: string;
  fullName?: string;
  email?: string;
  jobTitle?: string;
  source?: string;
  archiveReasonId?: string;
  offerStatus?: string;
  sortBy?: "submittedAt" | "matchScore";
  sortDir?: "asc" | "desc";
  searchMode?: "simple" | "boolean";
  scoreMin?: number;
  scoreMax?: number;
  /** A dashboard window: only applications that arrived within it. */
  period?: "1d" | "7d" | "30d" | "90d";
  /** The viewer's display zone, where the window's days begin. Sent with `period`. */
  tz?: string;
  /** A dashboard funnel band: applicants who reached this stage category or a later one. */
  reached?: "lead" | "applicant" | "interview" | "offer" | "hired";
}

/** Latest AI interview for an applicant, attached to the AI summary payload. */

export const applicantsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getApplicants: builder.query<ApplicantsResponse, ListApplicantsParams>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            searchParams.append(key, String(value));
          }
        });
        return {
          url: `/applicants?${searchParams.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["Applicants"],
    }),

    getScoringParams: builder.query<
      {
        data: {
          params: string[];
          configVersion: number;
          source: "job" | "instance" | "default";
          catalogue: ReadonlyArray<{ key: string; label: string; hint: string }>;
          minimum: number;
        };
      },
      { instanceId: string }
    >({
      query: ({ instanceId }) => ({
        url: `/applicants/scoring-params?instanceId=${instanceId}`,
        method: "GET",
      }),
      providesTags: [{ type: "Applicants", id: "scoring-params" }],
    }),

    setScoringParams: builder.mutation<
      { data: { params: string[]; configVersion: number }; rescored: boolean },
      { instanceId: string; params: string[] }
    >({
      query: ({ instanceId, params }) => ({
        url: `/applicants/scoring-params?instanceId=${instanceId}`,
        method: "PUT",
        body: { params },
      }),
      invalidatesTags: [{ type: "Applicants", id: "scoring-params" }],
    }),

    getScoringRubric: builder.query<
      { data: { rubric: string; maxLength: number } },
      { instanceId: string }
    >({
      query: ({ instanceId }) => ({
        url: `/applicants/scoring-rubric?instanceId=${instanceId}`,
        method: "GET",
      }),
      providesTags: [{ type: "Applicants", id: "scoring-rubric" }],
    }),

    setScoringRubric: builder.mutation<
      { data: { rubric: string }; rescored: boolean },
      { instanceId: string; rubric: string }
    >({
      query: ({ instanceId, rubric }) => ({
        url: `/applicants/scoring-rubric?instanceId=${instanceId}`,
        method: "PUT",
        body: { rubric },
      }),
      invalidatesTags: [{ type: "Applicants", id: "scoring-rubric" }],
    }),

    getApplicantById: builder.query<ApplicantDetail, { instanceId: string; applicantId: string }>({
      query: ({ instanceId, applicantId }) => ({
        url: `/applicants/${applicantId}?instanceId=${instanceId}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { applicantId }) => [{ type: "Applicants", id: applicantId }],
    }),

    getApplicantSources: builder.query<{ data: string[] }, { instanceId: string }>({
      query: ({ instanceId }) => ({
        url: `/applicants/sources?instanceId=${instanceId}`,
        method: "GET",
      }),
      providesTags: ["Applicants"],
    }),

    getApplicantPositions: builder.query<
      { data: Array<{ jobId: string; title: string; locationName: string | null }> },
      { instanceId: string } & ScopeFilterParams
    >({
      query: ({ instanceId, ...scope }) => ({
        url: "/applicants/positions",
        method: "GET",
        params: { instanceId, ...scope },
      }),
      providesTags: ["Applicants"],
    }),

    getApplicantStats: builder.query<
      ApplicantStats,
      {
        instanceId: string;
        jobId?: string;
        jobTitle?: string;
        period?: "1d" | "7d" | "30d" | "90d";
        tz?: string;
        reached?: "lead" | "applicant" | "interview" | "offer" | "hired";
      } & ScopeFilterParams
    >({
      query: ({
        instanceId,
        jobId,
        jobTitle,
        period,
        tz,
        reached,
        locationId,
        groupId,
        departmentId,
      }) => {
        const params = new URLSearchParams({ instanceId });
        if (jobId) params.set("jobId", jobId);
        if (jobTitle) params.set("jobTitle", jobTitle);
        if (period) params.set("period", period);
        if (period && tz) params.set("tz", tz);
        if (reached) params.set("reached", reached);
        // The Location and Department slice, so the cards count what the list shows.
        if (locationId !== undefined) params.set("locationId", String(locationId));
        if (groupId) params.set("groupId", groupId);
        if (departmentId) params.set("departmentId", departmentId);
        return {
          url: `/applicants/stats?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["Applicants"],
    }),

    getSecureFileUrl: builder.query<
      { success: boolean; downloadUrl: string; expiresInMinutes: number },
      { instanceId: string; fileUrl: string }
    >({
      query: ({ instanceId, fileUrl }) => {
        const isBlobPath = !fileUrl.startsWith("http://") && !fileUrl.startsWith("https://");
        const param = isBlobPath
          ? `blobPath=${encodeURIComponent(fileUrl)}`
          : `url=${encodeURIComponent(fileUrl)}`;
        return {
          url: `/files/download?instanceId=${instanceId}&${param}`,
          method: "GET",
        };
      },
    }),

    getViewerUrl: builder.query<
      {
        success: boolean;
        viewerUrl: string;
        viewerType: "native" | "google-docs";
        downloadUrl: string | null;
        fileExtension: string;
        expiresInMinutes: number;
      },
      { instanceId: string; fileUrl: string }
    >({
      query: ({ instanceId, fileUrl }) => {
        const isBlobPath = !fileUrl.startsWith("http://") && !fileUrl.startsWith("https://");
        const param = isBlobPath
          ? `blobPath=${encodeURIComponent(fileUrl)}`
          : `url=${encodeURIComponent(fileUrl)}`;
        return {
          url: `/files/viewer-url?instanceId=${instanceId}&${param}`,
          method: "GET",
        };
      },
    }),

    // Get inline proxy URL for displaying files without triggering download
    getInlineFileUrl: builder.query<string, { instanceId: string; blobPath: string }>({
      queryFn: ({ instanceId, blobPath }) => {
        const baseUrl = import.meta.env.VITE_API_URL || "";
        const proxyUrl = `${baseUrl}/api/v1/files/proxy?instanceId=${instanceId}&blobPath=${encodeURIComponent(blobPath)}`;
        return { data: proxyUrl };
      },
    }),

    createApplicant: builder.mutation<
      { success: boolean; applicant: Applicant },
      { instanceId: string; data: FormData }
    >({
      query: ({ instanceId, data }) => ({
        url: `/applicants/manual?instanceId=${instanceId}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Applicants"],
    }),

    parseResume: builder.mutation<
      {
        name?: string;
        email?: string;
        phone?: string;
        alternatePhone?: string;
        location?: string;
        currentTitle?: string;
        skills?: string[];
        linkedinUrl?: string;
        githubUrl?: string;
      },
      { instanceId: string; file: FormData }
    >({
      query: ({ instanceId, file }) => ({
        url: `/applicants/parse-resume?instanceId=${instanceId}`,
        method: "POST",
        body: file,
      }),
    }),

    /** The Hired details pop-up's own read: date of birth, gender, and whether greytHR needs them. */
    getHireDetails: builder.query<HireDetails, { instanceId: string; applicantId: string }>({
      query: ({ instanceId, applicantId }) =>
        `/applicants/${applicantId}/hire-details?instanceId=${instanceId}`,
      providesTags: (_result, _error, { applicantId }) => [
        { type: "Applicants", id: `${applicantId}-hire-details` },
        "GreythrPlan",
      ],
    }),

    updateApplicantStatus: builder.mutation<
      { success: boolean; previousStatus: string; newStatus: string },
      {
        instanceId: string;
        applicantId: string;
        status: string;
        archiveReasonId?: string;
        // Captured by the Hired details pop-up (YYYY-MM-DD). Only sent when
        // status === "hired"; hiringDate is required, joiningDate optional.
        hiringDate?: string;
        joiningDate?: string | null;
        // For greytHR, recorded only at hire; the server refuses them for any other stage.
        dateOfBirth?: string | null;
        gender?: GreythrGender | null;
        /** The dialog's "Create greytHR profile" choice; absent everywhere else (= on joining date). */
        greythrSend?: "joining_date" | "now" | "skip";
        /** The dialog's "Enrol in skyLearn" choice. Absent = the server's default for this path. */
        enrollInSkyLearn?: boolean;
      }
    >({
      query: ({
        instanceId,
        applicantId,
        status,
        archiveReasonId,
        hiringDate,
        joiningDate,
        dateOfBirth,
        gender,
        greythrSend,
        enrollInSkyLearn,
      }) => {
        const body: Record<string, unknown> = { status };
        if (archiveReasonId) body.archiveReasonId = archiveReasonId;
        if (hiringDate) body.hiringDate = hiringDate;
        if (joiningDate) body.joiningDate = joiningDate;
        if (dateOfBirth) body.dateOfBirth = dateOfBirth;
        if (gender) body.gender = gender;
        if (greythrSend) body.greythrSend = greythrSend;
        if (enrollInSkyLearn !== undefined) body.enrollInSkyLearn = enrollInSkyLearn;
        return {
          url: `/applicants/${applicantId}/status?instanceId=${instanceId}`,
          method: "PATCH",
          body,
        };
      },
      invalidatesTags: (_result, _error, { applicantId }) => [
        "Applicants",
        { type: "Applicants", id: applicantId },
        { type: "Applicants", id: `${applicantId}-history` },
        { type: "Applicants", id: `${applicantId}-hire-details` },
        // A hire, a decline or a revival changes the push history and its "to review" counts.
        "GreythrOutbox",
        // A hire schedules skyLearn onboarding; leaving Hired cancels it.
        { type: "SkyLearnOnboarding", id: applicantId },
        // Documents are collected only from someone hired: their Documents tab
        // and the Document Centre's list follow the stage.
        { type: "CandidateDocumentFolder", id: applicantId },
        "CandidateDocumentPeople",
      ],
    }),

    updateApplicantRating: builder.mutation<
      { success: boolean; rating: number | null; ratedBy: string },
      { instanceId: string; applicantId: string; rating: number | null }
    >({
      query: ({ instanceId, applicantId, rating }) => ({
        url: `/applicants/${applicantId}/rating?instanceId=${instanceId}`,
        method: "PATCH",
        body: { rating },
      }),
      invalidatesTags: (_result, _error, { applicantId }) => [
        "Applicants",
        { type: "Applicants", id: applicantId },
      ],
    }),

    updateApplicantTags: builder.mutation<
      { success: boolean; tags: string[] },
      { instanceId: string; applicantId: string; tags: string[] }
    >({
      query: ({ instanceId, applicantId, tags }) => ({
        url: `/applicants/${applicantId}/tags?instanceId=${instanceId}`,
        method: "PATCH",
        body: { tags },
      }),
      invalidatesTags: (_result, _error, { applicantId }) => [
        "Applicants",
        { type: "Applicants", id: applicantId },
      ],
    }),

    updateApplicantProfile: builder.mutation<
      { success: boolean },
      {
        instanceId: string;
        applicantId: string;
        data: {
          fullName?: string;
          email?: string;
          phone?: string | null;
          alternatePhone?: string | null;
        };
      }
    >({
      query: ({ instanceId, applicantId, data }) => ({
        url: `/applicants/${applicantId}/profile?instanceId=${instanceId}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { applicantId }) => [
        "Applicants",
        { type: "Applicants", id: applicantId },
      ],
    }),

    bulkUpdateStatus: builder.mutation<
      { success: boolean; updated: number; status: string },
      {
        instanceId: string;
        applicantIds: string[];
        status: string;
        archiveReasonId?: string;
        /** Moving to a hired stage: YYYY-MM-DD, from the bulk Hired dialog. */
        hiringDate?: string;
        joiningDate?: string;
      }
    >({
      query: ({ instanceId, applicantIds, status, archiveReasonId, hiringDate, joiningDate }) => ({
        url: `/applicants/bulk/status?instanceId=${instanceId}`,
        method: "PATCH",
        body: {
          applicantIds,
          status,
          ...(archiveReasonId ? { archiveReasonId } : {}),
          ...(hiringDate ? { hiringDate } : {}),
          ...(joiningDate ? { joiningDate } : {}),
        },
      }),
      // Documents follow the stage too (see updateApplicantStatus).
      invalidatesTags: [
        "Applicants",
        "CandidateDocumentFolder",
        "CandidateDocumentPeople",
        "SkyLearnOnboarding",
      ],
    }),

    bulkUpdateTags: builder.mutation<
      { success: boolean; updated: number; tags: string[]; action: string },
      { instanceId: string; applicantIds: string[]; tags: string[]; action: "add" | "replace" }
    >({
      query: ({ instanceId, applicantIds, tags, action }) => ({
        url: `/applicants/bulk/tags?instanceId=${instanceId}`,
        method: "PATCH",
        body: { applicantIds, tags, action },
      }),
      invalidatesTags: ["Applicants"],
    }),

    getStatusHistory: builder.query<
      Array<{
        id: string;
        previousStatus: string | null;
        newStatus: string;
        changedBy: string | null;
        changedAt: string;
        /** Set when an admin marked this change as recorded in error. */
        voidedAt: string | null;
        voidedBy: string | null;
        voidReason: string | null;
      }>,
      { instanceId: string; applicantId: string }
    >({
      query: ({ instanceId, applicantId }) => ({
        url: `/applicants/${applicantId}/status-history?instanceId=${instanceId}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { applicantId }) => [
        { type: "Applicants", id: `${applicantId}-history` },
      ],
    }),

    /**
     * Marks a stage change as recorded in error. Reports stop counting it, the
     * timeline keeps showing it struck through with the reason.
     */
    voidStatusHistory: builder.mutation<
      { data: { id: string; voided: boolean; restoredStatus: string | null } },
      { instanceId: string; applicantId: string; historyId: string; reason?: string }
    >({
      query: ({ instanceId, applicantId, historyId, reason }) => ({
        url: `/applicants/${applicantId}/status-history/${historyId}/void?instanceId=${instanceId}`,
        method: "POST",
        body: { reason },
      }),
      // The candidate's current stage moves too, so the applicant record and
      // the lists that show their stage have to refetch, not just the timeline.
      invalidatesTags: (_result, _error, { applicantId }) => [
        { type: "Applicants", id: `${applicantId}-history` },
        { type: "Applicants", id: applicantId },
        "Applicants",
        // The report counts this candidate's stage, so its figures are stale the
        // moment a stage change is voided.
        "ReportLayout",
        // Documents follow the stage too (see updateApplicantStatus).
        { type: "CandidateDocumentFolder", id: applicantId },
        "CandidateDocumentPeople",
      ],
    }),

    // `deleteDocumentRecords` also removes what the Document Centre holds for
    // them (the folder pointer, requests, upload records; never the files).
    // `documentRecordsDeleted` is false only when that was asked for and
    // failed: the applicant is deleted either way.
    deleteApplicant: builder.mutation<
      { success: boolean; message: string; documentRecordsDeleted?: boolean | null },
      { instanceId: string; applicantId: string; deleteDocumentRecords?: boolean }
    >({
      query: ({ instanceId, applicantId, deleteDocumentRecords }) => ({
        url: `/applicants/${applicantId}?instanceId=${instanceId}${
          deleteDocumentRecords ? "&deleteDocumentRecords=true" : ""
        }`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { applicantId }) => [
        "Applicants",
        { type: "Applicants", id: applicantId },
        { type: "Applicants", id: `${applicantId}-history` },
      ],
    }),

    bulkDeleteApplicants: builder.mutation<
      { success: boolean; deleted: number; documentRecordsDeleted?: boolean | null },
      { instanceId: string; applicantIds: string[]; deleteDocumentRecords?: boolean }
    >({
      query: ({ instanceId, applicantIds, deleteDocumentRecords }) => ({
        url: `/applicants/bulk?instanceId=${instanceId}`,
        method: "DELETE",
        body: deleteDocumentRecords
          ? { applicantIds, deleteDocumentRecords: true }
          : { applicantIds },
      }),
      invalidatesTags: ["Applicants"],
    }),

    considerForJob: builder.mutation<
      {
        success: boolean;
        newApplicationId: string;
        applicantName: string;
        targetJobTitle: string;
        removedFromSource: boolean;
      },
      { instanceId: string; applicantId: string; targetJobId: string; removeFromSource?: boolean }
    >({
      query: ({ instanceId, applicantId, targetJobId, removeFromSource }) => ({
        url: `/applicants/${applicantId}/consider-for-job?instanceId=${instanceId}`,
        method: "POST",
        body: { targetJobId, removeFromSource },
      }),
      invalidatesTags: ["Applicants"],
    }),

    getAiSummary: builder.query<
      {
        data: AiSummary;
        aiInterview?: AiSummaryInterview | null;
        cached: boolean;
      },
      { instanceId: string; applicantId: string }
    >({
      query: ({ instanceId, applicantId }) => ({
        url: `/applicants/${applicantId}/ai-summary?instanceId=${instanceId}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { applicantId }) => [
        { type: "Applicants", id: `${applicantId}-ai-summary` },
      ],
    }),

    regenerateAiSummary: builder.mutation<
      {
        data: AiSummary;
        aiInterview?: AiSummaryInterview | null;
        cached: boolean;
      },
      { instanceId: string; applicantId: string }
    >({
      query: ({ instanceId, applicantId }) => ({
        url: `/applicants/${applicantId}/ai-summary/regenerate?instanceId=${instanceId}`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { applicantId }) => [
        { type: "Applicants", id: `${applicantId}-ai-summary` },
      ],
    }),

    getInterviewQuestions: builder.query<
      { data: SuggestedInterviewQuestions | null },
      { instanceId: string; applicantId: string }
    >({
      query: ({ instanceId, applicantId }) => ({
        url: `/applicants/${applicantId}/interview-questions?instanceId=${instanceId}`,
        method: "GET",
      }),
    }),

    /** Generates and saves a fresh set; the saved query is updated in place, no refetch. */
    generateInterviewQuestions: builder.mutation<
      { data: SuggestedInterviewQuestions },
      { instanceId: string; applicantId: string }
    >({
      query: ({ instanceId, applicantId }) => ({
        url: `/applicants/${applicantId}/interview-questions?instanceId=${instanceId}`,
        method: "POST",
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(applicantsApi.util.upsertQueryData("getInterviewQuestions", args, data));
        } catch {
          // The component shows the error; the saved set stays as it was.
        }
      },
    }),

    regenerateVideoAnalysis: builder.mutation<
      { success: boolean; message: string },
      { instanceId: string; applicantId: string }
    >({
      query: ({ instanceId, applicantId }) => ({
        url: `/applicants/${applicantId}/video-analysis/regenerate?instanceId=${instanceId}`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { applicantId }) => [
        { type: "Applicants", id: applicantId },
      ],
    }),

    getNotificationContacts: builder.query<
      {
        email: string | null;
        emailSource: "form" | "account" | null;
        phone: string | null;
        phoneSource: "form" | "account" | null;
        smsConsent: boolean;
        isManualEntry: boolean;
        hasCandidateAccount: boolean;
        fallbackLink: string;
      },
      { instanceId: string; applicantId: string }
    >({
      query: ({ instanceId, applicantId }) => ({
        url: `/applicants/${applicantId}/notification-contacts?instanceId=${instanceId}`,
        method: "GET",
      }),
    }),

    requestVideoReupload: builder.mutation<
      {
        success: boolean;
        email: { sent: boolean; error?: string };
        sms: { sent: boolean; error?: string };
      },
      {
        instanceId: string;
        applicantId: string;
        channels: ("email" | "sms")[];
        email?: {
          subject: string;
          body: string;
          signatureId?: string;
          attachments?: Array<{
            content: string;
            filename: string;
            type?: string;
            disposition?: "attachment" | "inline";
          }>;
        };
        sms?: { body: string };
      }
    >({
      query: ({ instanceId, applicantId, channels, email, sms }) => ({
        url: `/applicants/${applicantId}/request-video-reupload?instanceId=${instanceId}`,
        method: "POST",
        body: { channels, email, sms },
      }),
    }),

    getMatchScore: builder.query<
      { data: MatchScoreDetail; cached: boolean },
      { instanceId: string; applicantId: string; force?: boolean }
    >({
      query: ({ instanceId, applicantId, force }) => ({
        url: `/applicants/${applicantId}/match-score?instanceId=${instanceId}${force ? "&force=true" : ""}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { applicantId }) => [{ type: "MatchScore", id: applicantId }],
    }),
  }),
});

export const {
  useGetApplicantsQuery,
  useLazyGetApplicantsQuery,
  useGetApplicantByIdQuery,
  useGetApplicantSourcesQuery,
  useGetApplicantPositionsQuery,
  useGetScoringParamsQuery,
  useSetScoringParamsMutation,
  useGetScoringRubricQuery,
  useSetScoringRubricMutation,
  useGetApplicantStatsQuery,
  useLazyGetSecureFileUrlQuery,
  useLazyGetViewerUrlQuery,
  useGetInlineFileUrlQuery,
  useCreateApplicantMutation,
  useParseResumeMutation,
  useUpdateApplicantStatusMutation,
  useGetHireDetailsQuery,
  useUpdateApplicantRatingMutation,
  useUpdateApplicantTagsMutation,
  useUpdateApplicantProfileMutation,
  useBulkUpdateStatusMutation,
  useBulkUpdateTagsMutation,
  useGetStatusHistoryQuery,
  useVoidStatusHistoryMutation,
  useDeleteApplicantMutation,
  useBulkDeleteApplicantsMutation,
  useConsiderForJobMutation,
  useGetAiSummaryQuery,
  useLazyGetAiSummaryQuery,
  useRegenerateAiSummaryMutation,
  useGetInterviewQuestionsQuery,
  useGenerateInterviewQuestionsMutation,
  useRegenerateVideoAnalysisMutation,
  useGetNotificationContactsQuery,
  useRequestVideoReuploadMutation,
  useGetMatchScoreQuery,
  useLazyGetMatchScoreQuery,
} = applicantsApi;
